import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    MenuItem,
    Alert,
    Select,
    InputLabel,
    FormControl,
    Chip,
    OutlinedInput,
    Card,
    LinearProgress,
    useTheme,
    alpha,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const interestOptions = [
    "FIG Professional Development",
    "FIG Finance",
    "FIG Procurement Analysis",
    "FIG Point of Sale",
    "FIG Special Diets",
    "FIG Buying Cooperative",
    "Swan AI Labs",
    "Custom Integrations",
    "Software Development",
    "Other",
];

const initialState = {
    firstName: "",
    lastName: "",
    organization: "",
    email: "",
    phone: "",
    role: "",
    interest: [],
    details: "",
};

export default function Contact() {
    const theme = useTheme();
    const [form, setForm] = useState(initialState);
    const [status, setStatus] = useState({ success: null, error: null, loading: false });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({
            ...form,
            [name]: name === "interest" ? (typeof value === "string" ? value.split(",") : value) : value,
        });
    };

    const validate = () => {
        if (
            !form.firstName.trim() ||
            !form.lastName.trim() ||
            !form.organization.trim() ||
            !form.email.trim() ||
            !form.phone.trim() ||
            !form.details.trim()
        ) return false;
        if (!/\S+@\S+\.\S+/.test(form.email)) return false;
        if (!/\d{7,}/.test(form.phone)) return false;
        return true;
    };

    const buildHtml = () => `
    <div style="font-family:Segoe UI,Arial,sans-serif;max-width:600px;margin:0 auto;">
      <h2 style="background:#21dbb8;padding:18px 0;color:#054067;text-align:center;margin-top:0;">
        New Swan Solutions Contact Request
      </h2>
      <table style="width:100%;font-size:1rem;border-collapse:collapse;">
        <tr><td style="font-weight:bold;padding:8px;">First Name:</td><td>${form.firstName}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Last Name:</td><td>${form.lastName}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">District/Organization:</td><td>${form.organization}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Email:</td><td>${form.email}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Phone:</td><td>${form.phone}</td></tr>
        ${form.role ? `<tr><td style="font-weight:bold;padding:8px;">Role/Title:</td><td>${form.role}</td></tr>` : ""}
        ${form.interest.length ? `<tr><td style="font-weight:bold;padding:8px;">Interest:</td><td>${form.interest.join(", ")}</td></tr>` : ""}
        <tr><td style="font-weight:bold;padding:8px;">Details/Message:</td><td>${form.details.replace(/\n/g, "<br/>")}</td></tr>
      </table>
      <hr style="margin:30px 0 12px 0;">
      <div style="color:#7e8b99;font-size:0.96rem;">Contact form submitted from <b>swansolutions.com</b></div>
    </div>
  `;

    const buildText = () => `
New Swan Solutions Contact Request

First Name: ${form.firstName}
Last Name: ${form.lastName}
District/Organization: ${form.organization}
Email: ${form.email}
Phone: ${form.phone}
${form.role ? `Role/Title: ${form.role}` : ""}
${form.interest.length ? `Interest: ${form.interest.join(", ")}` : ""}
Details/Message:
${form.details}
  `;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            setStatus({ ...status, error: "Please fill out all required fields correctly." });
            return;
        }
        setStatus({ loading: true, error: null, success: null });
        try {
            const res = await fetch("https://mailgun.swancreate.com/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    to: "dusty@swank12.com,becki@figk-12.com",
                    subject: "New Contact Request — Swan Solutions Website",
                    html: buildHtml(),
                    text: buildText(),
                }),
            });
            if (res.ok) {
                setStatus({ loading: false, success: "Thank you! Your message has been sent.", error: null });
                setForm(initialState);
            } else {
                throw new Error();
            }
        } catch {
            setStatus({
                loading: false,
                error: "Sorry, there was a problem sending your message. Please try again.",
                success: null,
            });
        }
    };

    const inputStyles = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: alpha(theme.palette.common.white, 0.8),
            backdropFilter: "blur(8px)",
            transition: "all 0.2s ease",
            "&:hover": {
                backgroundColor: alpha(theme.palette.common.white, 0.95),
            },
            "&.Mui-focused": {
                backgroundColor: "#fff",
                boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.2)}`,
            },
            "& fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.15),
            },
            "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.3),
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.secondary.main,
            },
        },
    };

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: theme.palette.background.default }}>

            {/* HERO */}
            <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                sx={{
                    width: "100vw",
                    marginLeft: "calc(-50vw + 50%)",
                    background: `linear-gradient(180deg, 
                        #5cc8ed 0%, 
                        #3a9fd4 30%, 
                        ${theme.palette.primary.main} 70%, 
                        ${theme.palette.primary.dark} 100%)`,
                    py: { xs: 6, md: 8 },
                    textAlign: "center",
                    color: "#fff",
                }}
            >
                <Container maxWidth="md">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, duration: 0.4 }}
                    >
                        <Typography
                            variant="h2"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: "2rem", md: "2.8rem" },
                                mb: 1.5,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Get In Touch
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: "1rem", md: "1.15rem" },
                                color: "rgba(255,255,255,0.9)",
                                maxWidth: 480,
                                mx: "auto",
                            }}
                        >
                            Have a question or want to learn more? Send us a message and a real person will get back to you within one business day.
                        </Typography>
                    </motion.div>
                </Container>
            </Box>

            {/* FORM SECTION */}
            <Container maxWidth="sm" sx={{ py: { xs: 5, md: 7 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 25 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25, duration: 0.4 }}
                >
                    <Card
                        elevation={0}
                        sx={{
                            p: { xs: 3, md: 4 },
                            borderRadius: "20px",
                            backgroundColor: alpha(theme.palette.common.white, 0.85),
                            backdropFilter: "blur(16px)",
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                            boxShadow: `0 4px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        {/* Loading indicator */}
                        <AnimatePresence>
                            {status.loading && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    style={{ position: "absolute", top: 0, left: 0, right: 0 }}
                                >
                                    <LinearProgress
                                        sx={{
                                            height: 3,
                                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                            "& .MuiLinearProgress-bar": {
                                                backgroundColor: theme.palette.secondary.main,
                                            },
                                        }}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <form onSubmit={handleSubmit} autoComplete="off">
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                    gap: 2.5,
                                }}
                            >
                                <TextField
                                    required
                                    label="First Name"
                                    name="firstName"
                                    value={form.firstName}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={inputStyles}
                                />
                                <TextField
                                    required
                                    label="Last Name"
                                    name="lastName"
                                    value={form.lastName}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={inputStyles}
                                />
                                <Box sx={{ gridColumn: { sm: "span 2" } }}>
                                    <TextField
                                        required
                                        label="District or Organization"
                                        name="organization"
                                        value={form.organization}
                                        onChange={handleChange}
                                        fullWidth
                                        sx={inputStyles}
                                    />
                                </Box>
                                <TextField
                                    required
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={inputStyles}
                                />
                                <TextField
                                    required
                                    label="Phone"
                                    name="phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={inputStyles}
                                />
                                <TextField
                                    label="Your Role / Title"
                                    name="role"
                                    value={form.role}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={inputStyles}
                                />
                                <FormControl fullWidth sx={inputStyles}>
                                    <InputLabel id="interest-label">Interested In</InputLabel>
                                    <Select
                                        labelId="interest-label"
                                        multiple
                                        name="interest"
                                        value={form.interest}
                                        onChange={handleChange}
                                        input={<OutlinedInput label="Interested In" />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                {selected.map((val) => (
                                                    <Chip
                                                        key={val}
                                                        label={val}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                                                            color: theme.palette.primary.dark,
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    >
                                        {interestOptions.map((opt) => (
                                            <MenuItem key={opt} value={opt}>
                                                {opt}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Box sx={{ gridColumn: { sm: "span 2" } }}>
                                    <TextField
                                        required
                                        label="How can we help?"
                                        name="details"
                                        value={form.details}
                                        onChange={handleChange}
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        sx={inputStyles}
                                    />
                                </Box>
                                <Box sx={{ gridColumn: { sm: "span 2" }, mt: 1 }}>
                                    <motion.div
                                        whileHover={{ scale: 1.015 }}
                                        whileTap={{ scale: 0.985 }}
                                        transition={{ type: "spring", stiffness: 400, damping: 20 }}
                                    >
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            disabled={status.loading}
                                            endIcon={status.success ? <CheckCircleIcon /> : <SendIcon />}
                                            sx={{
                                                py: 1.5,
                                                fontSize: "1.05rem",
                                                fontWeight: 700,
                                                borderRadius: "12px",
                                                background: status.success
                                                    ? theme.palette.success.main
                                                    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                                                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                                                transition: "box-shadow 0.2s ease",
                                                "&:hover": {
                                                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
                                                },
                                            }}
                                        >
                                            {status.loading ? "Sending..." : status.success ? "Sent!" : "Send Message"}
                                        </Button>
                                    </motion.div>
                                </Box>
                            </Box>
                        </form>

                        {/* Alerts */}
                        <AnimatePresence>
                            {(status.success || status.error) && (
                                <motion.div
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.25 }}
                                >
                                    <Box sx={{ mt: 2.5 }}>
                                        {status.success && (
                                            <Alert severity="success" sx={{ borderRadius: "10px" }}>
                                                {status.success}
                                            </Alert>
                                        )}
                                        {status.error && (
                                            <Alert severity="error" sx={{ borderRadius: "10px" }}>
                                                {status.error}
                                            </Alert>
                                        )}
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Card>
                </motion.div>
            </Container>
        </Box>
    );
}